import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Step, StepLabel, Stepper,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useIsMobile from '../hooks/useIsMobile';
import QuestionRenduAdmin from './QuestionRenduAdmin';
import { manquantes, nettoyer, questionsVisibles, valider } from './visibilite';
import { Definition, FichierReponse, Section, ValeurReponse } from '../services/formulairesServices';

// Aperçu interactif « Tester le formulaire » : rejoue la définition COURANTE de l'éditeur (même non enregistrée) ou
// celle d'un modèle de la liste, avec le même moteur conditionnel que la page publique (visibilite.ts = moteur.js).
// Une section visible par écran (Stepper), Précédent / Suivant, « Envoyer (test) » = nettoyer puis valider sur les
// questions visibles : erreurs en ligne sur la bonne étape, ou message de succès + message de fin.
// AUCUN appel réseau, AUCUNE écriture : tout l'état vit ici et repart de zéro à chaque ouverture.

export interface TesterFormulaireDialogProps {
  open: boolean;
  definition: Definition;
  nomModele: string;
  onClose: () => void;
}

type Valeurs = Record<string, ValeurReponse>;
type Fichiers = Record<string, FichierReponse[]>;

const TesterFormulaireDialog: React.FC<TesterFormulaireDialogProps> = ({ open, definition, nomModele, onClose }) => {
  const isMobile = useIsMobile();
  const [valeurs, setValeurs] = useState<Valeurs>({});
  const [fichiers, setFichiers] = useState<Fichiers>({});
  // Section affichée (id) ; null = première section visible
  const [sectionCourante, setSectionCourante] = useState<string | null>(null);
  // Erreurs de la dernière tentative d'envoi, par id de question (effacées champ par champ à la saisie)
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [valide, setValide] = useState(false);

  const reinitialiser = () => {
    setValeurs({});
    setFichiers({});
    setSectionCourante(null);
    setErreurs({});
    setValide(false);
  };

  // État réinitialisé à chaque ouverture (setters inline : stables, hors dépendances)
  useEffect(() => {
    if (!open) return;
    setValeurs({});
    setFichiers({});
    setSectionCourante(null);
    setErreurs({});
    setValide(false);
  }, [open]);

  // Visibilité recalculée à chaque saisie (une passe, ordre du document)
  const { visibles, sectionsVisibles } = useMemo(() => questionsVisibles(definition, valeurs, fichiers), [definition, valeurs, fichiers]);
  const visiblesSet = useMemo(() => new Set(visibles), [visibles]);
  const nbManquantes = useMemo(() => manquantes(definition, valeurs, fichiers, visibles).length, [definition, valeurs, fichiers, visibles]);

  // Sections visibles, dans l'ordre du document (une par étape)
  const etapes: Section[] = useMemo(() => {
    const set = new Set(sectionsVisibles);
    return definition.sections.filter((s) => set.has(s.id));
  }, [definition, sectionsVisibles]);

  // Étape affichée : la section courante si elle est encore visible, sinon la section visible la plus proche (dans
  // l'ordre du document ; à égalité, la précédente) ; 0 si aucune section courante
  const indexEtape = useMemo(() => {
    if (!etapes.length) return 0;
    if (sectionCourante === null) return 0;
    const direct = etapes.findIndex((s) => s.id === sectionCourante);
    if (direct >= 0) return direct;
    const position = definition.sections.findIndex((s) => s.id === sectionCourante);
    if (position < 0) return 0;
    let meilleur = 0;
    let distance = Infinity;
    etapes.forEach((s, k) => {
      const d = Math.abs(definition.sections.indexOf(s) - position);
      if (d < distance) { distance = d; meilleur = k; }
    });
    return meilleur;
  }, [etapes, sectionCourante, definition]);

  const etape: Section | undefined = etapes[indexEtape];
  const derniere = indexEtape >= etapes.length - 1;
  // Seules les erreurs de questions encore visibles comptent (une question masquée depuis le dernier « Envoyer (test) »
  // n'est plus à corriger)
  const nbErreurs = Object.keys(erreurs).filter((id) => visiblesSet.has(id)).length;
  // Des erreurs subsistent sur d'autres étapes que celle affichée
  const erreursAilleurs = etapes.some((s, k) => k !== indexEtape && s.questions.some((q) => visiblesSet.has(q.id) && erreurs[q.id] !== undefined));

  const changerValeur = (id: string, v: ValeurReponse) => {
    setValeurs((prev) => ({ ...prev, [id]: v }));
    if (erreurs[id]) setErreurs((prev) => { const n = { ...prev }; delete n[id]; return n; });
    if (valide) setValide(false);
  };
  const changerFichiers = (id: string, f: FichierReponse[]) => {
    setFichiers((prev) => ({ ...prev, [id]: f }));
    if (erreurs[id]) setErreurs((prev) => { const n = { ...prev }; delete n[id]; return n; });
    if (valide) setValide(false);
  };

  const aller = (k: number) => {
    const cible = etapes[Math.max(0, Math.min(k, etapes.length - 1))];
    if (cible) setSectionCourante(cible.id);
  };

  // « Envoyer (test) » : même enchaînement que la soumission côté backend client (nettoyer → visibles → valider)
  const envoyer = () => {
    const propres = nettoyer(definition, valeurs);
    const vis = questionsVisibles(definition, propres, fichiers).visibles;
    const liste = valider(definition, propres, fichiers, vis);
    if (!liste.length) {
      setErreurs({});
      setValide(true);
      return;
    }
    const parQuestion: Record<string, string> = {};
    liste.forEach((e) => { if (parQuestion[e.question] === undefined) parQuestion[e.question] = e.message; });
    setErreurs(parQuestion);
    setValide(false);
    // Première étape (dans l'ordre du document) portant une erreur
    const premiere = etapes.find((s) => s.questions.some((q) => parQuestion[q.id] !== undefined));
    if (premiere) setSectionCourante(premiere.id);
  };

  const titreEtape = etape ? etape.titre || etape.id : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: isMobile ? {} : { height: '88vh' } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Tester « {nomModele} »</Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Fermer">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="warning" variant="outlined" sx={{ py: 0 }}>
          Mode test : rien n'est enregistré ni envoyé. Le formulaire se comporte comme pour le client (questions et
          sections conditionnelles, contrôles à l'envoi).
        </Alert>

        {etapes.length === 0 && (
          <Alert severity="info">Aucune section visible avec les réponses actuelles.</Alert>
        )}

        {etapes.length > 0 && (
          isMobile ? (
            <Typography variant="subtitle2" color="text.secondary">
              Étape {indexEtape + 1} / {etapes.length} — {titreEtape}
            </Typography>
          ) : (
            <Stepper activeStep={indexEtape} alternativeLabel>
              {etapes.map((s) => (
                <Step key={s.id}>
                  <StepLabel>{s.titre || s.id}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )
        )}

        {valide ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="success">Formulaire valide — aucune donnée n'a été enregistrée ni envoyée.</Alert>
            {definition.message_fin && (
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{definition.message_fin}</Typography>
            )}
          </Box>
        ) : etape ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {indexEtape === 0 && definition.intro && (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{definition.intro}</Typography>
            )}
            {nbErreurs > 0 && (
              <Alert severity="error">
                {nbErreurs === 1 ? 'Une réponse est à corriger' : `${nbErreurs} réponses sont à corriger`}
                {erreursAilleurs ? " (y compris sur d'autres étapes : voir les champs signalés)." : ' : voir les champs signalés.'}
              </Alert>
            )}
            {!isMobile && <Typography variant="h6">{titreEtape}</Typography>}
            {etape.questions.filter((q) => visiblesSet.has(q.id)).map((q) => (
              <QuestionRenduAdmin
                key={q.id}
                question={q}
                valeur={valeurs[q.id]}
                fichiers={fichiers[q.id] || []}
                erreur={erreurs[q.id]}
                onChange={(v) => changerValeur(q.id, v)}
                onFichiersChange={(f) => changerFichiers(q.id, f)}
              />
            ))}
            {etape.questions.every((q) => !visiblesSet.has(q.id)) && (
              <Typography variant="body2" color="text.secondary">
                Aucune question de cette section n'est affichée avec les réponses actuelles.
              </Typography>
            )}
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, pl: 1 }}>
          {nbManquantes === 0 ? 'Aucune réponse obligatoire manquante' : `${nbManquantes} réponse${nbManquantes > 1 ? 's' : ''} obligatoire${nbManquantes > 1 ? 's' : ''} manquante${nbManquantes > 1 ? 's' : ''}`}
        </Typography>
        <Button onClick={reinitialiser} color="inherit">Réinitialiser</Button>
        {valide && <Button onClick={() => setValide(false)}>Modifier les réponses</Button>}
        {!valide && etapes.length > 0 && (
          <>
            <Button onClick={() => aller(indexEtape - 1)} disabled={indexEtape === 0}>Précédent</Button>
            {derniere ? (
              <Button variant="contained" onClick={envoyer}>Envoyer (test)</Button>
            ) : (
              <Button variant="contained" onClick={() => aller(indexEtape + 1)}>Suivant</Button>
            )}
          </>
        )}
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TesterFormulaireDialog;
